// src/components/ui/forms/ServiceCreateForm/ServiceCreateFormShadcn.jsx
import React, { useState } from 'react';
import {
  X,
  Save,
  Wrench,
  Type,
  FileText,
  Palette
} from 'lucide-react';

// shadcn components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../dialog';
import { Button } from '../../button';
import { Input } from '../../input';
import { Label } from '../../label';
import { Textarea } from '../../textarea';
import { Switch } from '../../switch';

import { cn } from '../../../../utils/cn';

const ServiceCreateFormShadcn = ({
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color_code: '#8B5CF6',
    active: true,
    requires_confirmation: false
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Color options
  const colorOptions = [
    '#8B5CF6', // Purple
    '#06B6D4', // Cyan
    '#10B981', // Emerald
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#EC4899', // Pink
    '#8B5A2B', // Brown
    '#6366F1'  // Indigo
  ];

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Service name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData = {
      name: formData.name.trim(),
      description: formData.description?.trim() || null,
      base_price: null,
      base_duration: 60,
      color_code: formData.color_code,
      active: formData.active,
      requires_confirmation: formData.requires_confirmation
    };

    onSave(submitData);
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Wrench className="w-5 h-5 text-primary" />
            </div>
            Create New Service
          </DialogTitle>
          <DialogDescription>
            Create a new service. You can set pricing and duration in the Pricing & Duration tab after creation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Service Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <Type className="w-4 h-4" />
              Service Name
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="Enter service name..."
              className={cn(
                validationErrors.name && "border-red-500 focus-visible:ring-red-500"
              )}
              autoFocus
              required
            />
            {validationErrors.name && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <span className="w-4 h-4 rounded-full bg-red-500/20 flex items-center justify-center text-xs">!</span>
                {validationErrors.name}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Description
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Describe what this service includes..."
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Color Picker */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Service Color
            </Label>
            <div className="flex gap-2 flex-wrap">
              {colorOptions.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleFieldChange('color_code', color)}
                  className={cn(
                    "w-8 h-8 rounded-lg border-2 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary/50",
                    formData.color_code === color
                      ? "border-primary scale-110 shadow-lg"
                      : "border-border hover:border-muted-foreground"
                  )}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>

          {/* Toggle Settings */}
          <div className="space-y-4">
            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
              <div className="space-y-1">
                <Label className="text-base font-medium">Service Active</Label>
                <p className="text-sm text-muted-foreground">Make this service available for booking</p>
              </div>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => handleFieldChange('active', checked)}
              />
            </div>

            {/* Requires Confirmation Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
              <div className="space-y-1">
                <Label className="text-base font-medium">Requires Confirmation</Label>
                <p className="text-sm text-muted-foreground">Appointments need manual approval before booking</p>
              </div>
              <Switch
                checked={formData.requires_confirmation}
                onCheckedChange={(checked) => handleFieldChange('requires_confirmation', checked)}
              />
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !formData.name?.trim()}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Create Service
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceCreateFormShadcn;